import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import { formatPrice } from "constants/massages";
import { useTeamAdminBrand } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import {
  formatLong,
  formatShortDate,
  sourceLabel,
  statusLabel,
  translateServiceLabel,
} from "./formatters";
import { useTeamAdminLiveOptional } from "./TeamAdminLiveContext";
import TeamDeleteBookingDialog from "./TeamDeleteBookingDialog";

const CustomersTab = () => {
  const { t, dateLocale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pageRef = useRef(1);
  const live = useTeamAdminLiveOptional();
  const isSuperAdmin = useSelector((s) => Boolean(s.user?.isSuperAdmin));
  const [deleteCustomerTarget, setDeleteCustomerTarget] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [creditAmountEuro, setCreditAmountEuro] = useState("");
  const [addingCredit, setAddingCredit] = useState(false);
  const [creditMsg, setCreditMsg] = useState(null);
  const [exporting, setExporting] = useState(false);

  const closeDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
    setCreditAmountEuro("");
    setCreditMsg(null);
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") closeDetail();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeDetail, selectedId]);

  const load = useCallback(
    async (page = 1, { silent = false } = {}) => {
      pageRef.current = page;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/customers", {
          params: {
            ...adminBrandParams({ brand: adminBrand }),
            page,
            limit: 20,
            search: search || undefined,
          },
        });
        setCustomers(data.customers || []);
        setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || t("customers.loadError"));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [search, adminBrand, t]
  );

  useEffect(() => {
    load(1, { silent: false });
  }, [load]);

  useEffect(() => {
    if (!live?.refreshTick) return;
    load(pageRef.current, { silent: true });
  }, [live?.refreshTick, load]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    setCreditAmountEuro("");
    setCreditMsg(null);
    try {
      const { data } = await api.get(`/admin/customers/${id}`, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      setDetail(data);
    } catch (err) {
      setError(err?.response?.data?.message || t("customers.detailError"));
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReferralCredit = async (event) => {
    event.preventDefault();
    if (!selectedId || !creditAmountEuro.trim()) return;
    setAddingCredit(true);
    setCreditMsg(null);
    setError(null);
    try {
      const { data } = await api.post(
        `/admin/customers/${selectedId}/referral-balance`,
        { amountEuro: creditAmountEuro.trim() },
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      setDetail((prev) =>
        prev ? { ...prev, customer: data.customer || prev.customer } : prev
      );
      setCreditAmountEuro("");
      setCreditMsg({ kind: "success", text: data.message });
    } catch (err) {
      setCreditMsg({
        kind: "error",
        text: err?.response?.data?.message || t("customers.referralCreditError"),
      });
    } finally {
      setAddingCredit(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await api.get("/admin/customers/export", {
        params: adminBrandParams({ brand: adminBrand }),
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"];
      let filename = "kunden.csv";
      const match =
        disposition && /filename="?([^";\n]+)"?/i.exec(disposition);
      if (match) filename = match[1];
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = t("customers.exportError");
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed.message) message = parsed.message;
        } catch {
          /* keep default message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!deleteCustomerTarget) return;
    setDeletingCustomer(true);
    setError(null);
    try {
      await api.delete(`/admin/customers/${deleteCustomerTarget._id}`, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      setCustomers((prev) =>
        prev.filter((c) => c._id !== deleteCustomerTarget._id)
      );
      setPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));
      if (selectedId === deleteCustomerTarget._id) {
        closeDetail();
      }
      setDeleteCustomerTarget(null);
    } catch (err) {
      setError(err?.response?.data?.message || t("customers.deleteError"));
    } finally {
      setDeletingCustomer(false);
    }
  };

  return (
    <div className="team-customers">
      <p className="team-lead">{t("customers.lead")}</p>

      <form
        className="team-search"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(searchInput.trim());
        }}
      >
        <input
          type="search"
          placeholder={t("customers.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="btn-primary">
          {t("common.search")}
        </button>
        {search && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              setSearchInput("");
              setSearch("");
            }}
          >
            {t("common.reset")}
          </button>
        )}
        <button
          type="button"
          className="btn-ghost"
          disabled={exporting || loading}
          onClick={exportCsv}
        >
          {exporting ? t("customers.exporting") : t("customers.exportCsv")}
        </button>
      </form>

      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="team-customers-layout">
        <div className="team-customers-list">
          {loading ? (
            <div className="team-empty">{t("customers.loading")}</div>
          ) : customers.length === 0 ? (
            <div className="team-empty">{t("customers.empty")}</div>
          ) : (
            <ul className="team-customer-rows">
              {customers.map((c) => (
                <li key={c._id}>
                  <button
                    type="button"
                    className={`team-customer-row ${selectedId === c._id ? "is-active" : ""}`}
                    onClick={() => openDetail(c._id)}
                  >
                    <div>
                      <strong>
                        {c.firstName} {c.lastName}
                      </strong>
                      <span className="team-muted">{c.email}</span>
                    </div>
                    <div className="team-customer-row-meta">
                      <span className="badge">
                        {sourceLabel(t, c.source, c.registrationSite)}
                      </span>
                      <span>{t("customers.bookingCount", { count: c.bookingCount })}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {pagination.pages > 1 && (
            <div className="team-pagination">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => load(pagination.page - 1)}
              >
                ←
              </button>
              <span>
                {pagination.page}/{pagination.pages}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.pages}
                onClick={() => load(pagination.page + 1)}
              >
                →
              </button>
            </div>
          )}
        </div>

        {selectedId && (
          <button
            type="button"
            className="team-customer-detail-backdrop"
            onClick={closeDetail}
            aria-label={t("common.close")}
          />
        )}

        <aside
          className={`team-customer-detail ${selectedId ? "is-open" : ""}`}
          role={selectedId ? "dialog" : "complementary"}
          aria-modal={selectedId ? "true" : undefined}
          aria-labelledby={detail ? "team-customer-detail-title" : undefined}
        >
          {!selectedId ? (
            <div className="team-empty">{t("customers.selectHint")}</div>
          ) : detailLoading ? (
            <div className="team-empty">{t("customers.detailLoading")}</div>
          ) : detail ? (
            <>
              <div className="team-detail-header">
                <h3 id="team-customer-detail-title">
                  {detail.customer.firstName} {detail.customer.lastName}
                </h3>
                <div className="team-detail-header-actions">
                  {isSuperAdmin && (
                    <button
                      type="button"
                      className="team-delete-btn team-delete-btn--permanent"
                      disabled={deletingCustomer}
                      onClick={() => setDeleteCustomerTarget(detail.customer)}
                    >
                      {t("customers.permanentDelete")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="team-close"
                    onClick={closeDetail}
                    aria-label={t("common.close")}
                  >
                    ×
                  </button>
                </div>
              </div>
              <dl className="team-detail-dl">
                <dt>{t("customers.field.email")}</dt>
                <dd>{detail.customer.email}</dd>
                <dt>{t("customers.field.phone")}</dt>
                <dd>{detail.customer.phone}</dd>
                <dt>{t("customers.field.registered")}</dt>
                <dd>{formatShortDate(detail.customer.createdAt, dateLocale)}</dd>
                <dt>{t("customers.field.source")}</dt>
                <dd>
                  {sourceLabel(
                    t,
                    detail.customer.source,
                    detail.customer.registrationSite
                  )}
                </dd>
                <dt>{t("customers.field.freeChair")}</dt>
                <dd>
                  {detail.customer.freeChairBookingUsed
                    ? t("customers.freeChair.used")
                    : t("customers.freeChair.available")}
                </dd>
                <dt>{t("customers.field.emailVerified")}</dt>
                <dd>
                  {detail.customer.emailVerified ? t("common.yes") : t("common.no")}
                </dd>
                <dt>{t("customers.field.referralLink")}</dt>
                <dd>
                  {detail.customer.referralLink ? (
                    <a href={detail.customer.referralLink} target="_blank" rel="noreferrer">
                      {detail.customer.referralLink}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt>{t("customers.field.referralUses")}</dt>
                <dd>{detail.customer.referralUseCount ?? 0}</dd>
                <dt>{t("customers.field.referralBalance")}</dt>
                <dd>{formatPrice(detail.customer.referralBalanceCents || 0)}</dd>
              </dl>
              <form className="team-referral-credit-form" onSubmit={submitReferralCredit}>
                <h4>{t("customers.referralCreditTitle")}</h4>
                <p className="team-muted">{t("customers.referralCreditLead")}</p>
                <div className="team-referral-credit-row">
                  <label htmlFor="team-referral-credit-amount">
                    {t("customers.referralCreditAmount")}
                  </label>
                  <div className="team-referral-credit-inputs">
                    <input
                      id="team-referral-credit-amount"
                      type="text"
                      inputMode="decimal"
                      placeholder="5,00"
                      value={creditAmountEuro}
                      onChange={(e) => setCreditAmountEuro(e.target.value)}
                      disabled={addingCredit}
                    />
                    <span className="team-referral-credit-suffix">€</span>
                    <button type="submit" className="btn-primary" disabled={addingCredit}>
                      {addingCredit
                        ? t("customers.referralCreditSubmitting")
                        : t("customers.referralCreditSubmit")}
                    </button>
                  </div>
                </div>
                {creditMsg && (
                  <div
                    className={`auth-alert ${creditMsg.kind === "error" ? "is-error" : "is-success"}`}
                  >
                    {creditMsg.text}
                  </div>
                )}
              </form>
              <p className="team-readonly-note">{t("customers.readonlyNote")}</p>
              <h4>{t("customers.bookingHistory")}</h4>
              {detail.bookings?.length === 0 ? (
                <p className="team-muted">{t("customers.noBookings")}</p>
              ) : (
                <ul className="team-detail-bookings">
                  {detail.bookings.map((b) => (
                    <li key={b._id}>
                      <strong>{translateServiceLabel(t, b)}</strong>
                      <span className="team-muted">{formatLong(b.start, dateLocale)}</span>
                      <span className="badge">{statusLabel(t, b.status)}</span>
                      <span className="team-muted">
                        {b.priceCents === 0
                          ? t("common.free")
                          : formatPrice(b.priceCents)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </aside>
      </div>

      <TeamDeleteBookingDialog
        open={Boolean(deleteCustomerTarget)}
        subject={
          deleteCustomerTarget
            ? {
                name: `${deleteCustomerTarget.firstName || ""} ${deleteCustomerTarget.lastName || ""}`.trim(),
                subtitle: deleteCustomerTarget.email,
              }
            : null
        }
        variant="customer"
        dateLocale={dateLocale}
        t={t}
        busy={deletingCustomer}
        onClose={() => !deletingCustomer && setDeleteCustomerTarget(null)}
        onConfirm={confirmDeleteCustomer}
      />
    </div>
  );
};

export default CustomersTab;
